"use client";

/**
 * ADR 0418 D5 ("ทาง b") — the ONE writer of `document.title` and the tab icon.
 * plan: 4-Archives/tms/deeptrack-plan/0418-one-web-brand-by-hostname.md
 *
 * Next does not own either any more: no `title` in any route's `metadata`, no `app/favicon.ico`.
 * Proven 2026-10-07 on the CCA static build: when Next owns them it writes its baked values back
 * on every client navigation (`router.push`), so a runtime brand lasted one page only.
 * Re-applying on `pathname` is cheap insurance in case something else touches <head> later.
 *
 * The icon is swapped by REMOVING the old <link> and adding a new one — some Safari versions
 * ignore an `href` change on an existing icon link (not yet verified on real Safari).
 */
import { useEffect } from "react";
import { usePathname } from "next/navigation";
import type { BrandState } from "@/lib/brand-state";
import { headFor } from "@/lib/host-brand";

export function BrandHead({ state }: { state: BrandState }) {
  const pathname = usePathname();
  const { title, faviconHref } = headFor(state);

  useEffect(() => {
    if (document.title !== title) document.title = title;

    const icons = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
    const ours = icons.length === 1 && icons[0].hasAttribute("data-brand-icon") ? icons[0] : null;
    if (ours && ours.getAttribute("href") === faviconHref) return;
    icons.forEach((l) => l.remove());
    const link = document.createElement("link");
    link.rel = "icon";
    link.href = faviconHref;
    link.setAttribute("data-brand-icon", "");
    document.head.appendChild(link);
  }, [title, faviconHref, pathname]);

  return null;
}
